/**
 * Impulse OS 2.0 Theme — theme.js
 * Phase 3 / Full JavaScript
 *
 * Modules:
 *  1. Utilities
 *  2. Sticky Header
 *  3. Mobile Navigation
 *  4. Cart Drawer
 *  5. Product Gallery (PDP thumb switcher)
 *  6. Quantity Buttons (PDP + Cart)
 *  7. Variant Selector (PDP)
 *  8. Product Details Accordion (PDP)
 *  9. Add-to-Cart (ATC) handler
 * 10. Quick View
 * 11. Announcement Bar dismiss
 * 12. CLI Docs — copy button + active nav
 * 13. Collection sort redirect
 * 14. Cart item quantity update
 * 15. Scroll-reveal animations
 * 16. Predictive Search
 * 17. Init
 */

'use strict';

/* ==========================================================
   1. Utilities
   ========================================================== */

const utils = {
  debounce(fn, wait = 200) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  },
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },
  trapFocus(element) {
    const focusable = element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }
    element.addEventListener('keydown', handler);
    return () => element.removeEventListener('keydown', handler);
  },
  serializeForm(form) {
    return new URLSearchParams(new FormData(form)).toString();
  },
  async fetchJSON(url, options = {}) {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      ...options,
    });
    if (!res.ok) throw new Error(`Fetch error ${res.status}: ${url}`);
    return res.json();
  },
};


/* ==========================================================
   2. Sticky Header
   ========================================================== */

class StickyHeader {
  constructor() {
    this.header = document.querySelector('.site-header');
    if (!this.header) return;
    this.init();
  }
  init() {
    const onScroll = utils.debounce(() => {
      if (window.scrollY > 10) {
        this.header.classList.add('site-header--scrolled');
      } else {
        this.header.classList.remove('site-header--scrolled');
      }
    }, 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
}


/* ==========================================================
   3. Mobile Navigation
   ========================================================== */

class MobileNav {
  constructor() {
    this.toggle   = document.querySelector('.mobile-nav-toggle');
    this.nav      = document.querySelector('.mobile-nav');
    this.overlay  = document.querySelector('.mobile-nav__overlay');
    this.closeBtn = document.querySelector('.mobile-nav__close');
    this.subToggles = document.querySelectorAll('[data-mobile-sub-toggle]');
    if (!this.toggle || !this.nav) return;
    this.removeFocusTrap = null;
    this.init();
  }
  open() {
    this.nav.classList.add('is-open');
    this.overlay && this.overlay.classList.add('is-visible');
    this.toggle.classList.add('is-active');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.removeFocusTrap = utils.trapFocus(this.nav);
    const firstLink = this.nav.querySelector('a, button');
    firstLink && firstLink.focus();
  }
  close() {
    this.nav.classList.remove('is-open');
    this.overlay && this.overlay.classList.remove('is-visible');
    this.toggle.classList.remove('is-active');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.removeFocusTrap && this.removeFocusTrap();
    this.toggle.focus();
  }
  init() {
    this.toggle.addEventListener('click', () => {
      this.nav.classList.contains('is-open') ? this.close() : this.open();
    });
    this.closeBtn  && this.closeBtn.addEventListener('click',  () => this.close());
    this.overlay   && this.overlay.addEventListener('click',   () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.nav.classList.contains('is-open')) this.close();
    });
    this.subToggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = document.querySelector(btn.dataset.mobileSubToggle);
        if (!target) return;
        const isOpen = target.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }
}


/* ==========================================================
   4. Cart Drawer
   ========================================================== */

class CartDrawer {
  constructor() {
    this.drawer   = document.getElementById('cart-drawer');
    this.body     = this.drawer ? this.drawer.querySelector('.cart-drawer__body') : null;
    this.closeBtn = this.drawer ? this.drawer.querySelector('.cart-drawer__close') : null;
    this.overlay  = this.drawer ? this.drawer.querySelector('.cart-drawer__overlay') : null;
    this.toggleBtns = document.querySelectorAll('.cart-toggle');
    if (!this.drawer) return;
    this.removeFocusTrap = null;
    this.init();
  }
  async open() {
    await this.refresh();
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.removeFocusTrap = utils.trapFocus(this.drawer.querySelector('.cart-drawer__inner'));
    const firstFocusable = this.drawer.querySelector('button, a, input');
    firstFocusable && firstFocusable.focus();
  }
  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.removeFocusTrap && this.removeFocusTrap();
  }
  async refresh() {
    try {
      const cart = await utils.fetchJSON('/cart.js');
      this.renderBody(cart);
      this.updateCartCount(cart.item_count);
    } catch (err) {
      console.warn('[CartDrawer] refresh failed:', err);
    }
  }
  updateCartCount(count) {
    document.querySelectorAll('.cart-count').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }
  renderBody(cart) {
    if (!this.body) return;
    if (cart.item_count === 0) {
      this.body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      return;
    }
    const FREE_SHIP = 10000;
    const cdRemaining = Math.max(0, FREE_SHIP - cart.total_price);
    const cdPct = Math.min(100, Math.round(cart.total_price * 100 / FREE_SHIP));
    const cdFmt = (c) => '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const hasFreight = cart.items.some((item) => {
      const t = (item.product_type || '').toLowerCase();
      return t.includes('commercial') || t.includes('industrial');
    });
    const shippingBar = hasFreight ? '' : cdRemaining > 0
      ? `<div class="cd-ship-bar"><p class="cd-ship-bar__msg">Add <strong>${cdFmt(cdRemaining)}</strong> more for <strong>FREE shipping!</strong></p><div class="cd-ship-bar__track"><div class="cd-ship-bar__fill" style="width:${cdPct}%"></div></div></div>`
      : `<div class="cd-ship-bar cd-ship-bar--done"><p class="cd-ship-bar__msg">&#127881; Your order qualifies for <strong>FREE shipping!</strong></p><div class="cd-ship-bar__track"><div class="cd-ship-bar__fill" style="width:100%"></div></div></div>`;
    const itemsHTML = cart.items.map((item) => `
      <div class="cart-item" data-line="${item.handle}">
        <div class="cart-item__image">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy" width="88" height="88">` : ''}
        </div>
        <div class="cart-item__details">
          ${item.vendor ? `<p class="cart-item__vendor">${item.vendor}</p>` : ''}
          <p class="cart-item__title"><a href="${item.url}">${item.product_title}</a></p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <div class="cart-item__footer">
            <div class="cart-item__qty">
              <button class="cart-item__qty-btn" data-qty-change="-1" data-variant="${item.variant_id}" aria-label="Decrease quantity">−</button>
              <input class="cart-item__qty-input" type="number" value="${item.quantity}" min="0" data-variant="${item.variant_id}" aria-label="Quantity">
              <button class="cart-item__qty-btn" data-qty-change="1" data-variant="${item.variant_id}" aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-item__price">${utils.formatMoney(item.final_line_price)}</span>
          </div>
          <button class="cart-item__remove" data-remove-variant="${item.variant_id}">Remove</button>
        </div>
      </div>
    `).join('');
    const footer = this.drawer.querySelector('.cart-drawer__footer');
    if (footer) {
      footer.innerHTML = `
        <div style="display:flex;justify-content:space-between;font-weight:700;font-size:16px;margin-bottom:12px;">
          <span>Subtotal</span>
          <span>${utils.formatMoney(cart.total_price)}</span>
        </div>
        <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
        <a href="/cart"     class="btn btn--secondary btn--full">View Cart</a>
      `;
    }
    this.body.innerHTML = shippingBar + itemsHTML;
    this.bindDrawerQty();
  }
  bindDrawerQty() {
    if (!this.body) return;
    this.body.querySelectorAll('[data-qty-change]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.variant;
        const input = this.body.querySelector(`.cart-item__qty-input[data-variant="${variantId}"]`);
        if (!input) return;
        const newQty = Math.max(0, parseInt(input.value, 10) + parseInt(btn.dataset.qtyChange, 10));
        await this.updateQty(variantId, newQty);
      });
    });
    this.body.querySelectorAll('[data-remove-variant]').forEach((btn) => {
      btn.addEventListener('click', () => this.updateQty(btn.dataset.removeVariant, 0));
    });
    this.body.querySelectorAll('.cart-item__qty-input').forEach((input) => {
      input.addEventListener('change', async () => {
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        await this.updateQty(input.dataset.variant, qty);
      });
    });
  }
  async updateQty(variantId, qty) {
    try {
      await utils.fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: variantId, quantity: qty }),
      });
      await this.refresh();
    } catch (err) {
      console.warn('[CartDrawer] updateQty failed:', err);
    }
  }
  init() {
    if (!document.getElementById('cd-ship-css')) {
      const s = document.createElement('style');
      s.id = 'cd-ship-css';
      s.textContent = '.cd-ship-bar{background:#f0f8ff;border:1px solid #c5dff5;border-radius:8px;padding:12px 16px;margin-bottom:14px;text-align:center}.cd-ship-bar__msg{font-size:13px;margin:0 0 8px;color:#003366;line-height:1.4}.cd-ship-bar--done .cd-ship-bar__msg{color:#1e7e1e}.cd-ship-bar__track{background:#dce8f5;border-radius:50px;height:8px;overflow:hidden}.cd-ship-bar__fill{background:linear-gradient(90deg,#003366 0%,#007bbd 100%);height:100%;border-radius:50px;min-width:4px;transition:width .3s ease}';
      document.head.appendChild(s);
    }
    this.toggleBtns.forEach((btn) => btn.addEventListener('click', () => this.open()));
    this.closeBtn  && this.closeBtn.addEventListener('click',  () => this.close());
    this.overlay   && this.overlay.addEventListener('click',   () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.getAttribute('aria-hidden') === 'false') this.close();
    });
  }
}


/* ==========================================================
   5. Product Gallery (PDP)
   ========================================================== */

class ProductGallery {
  constructor() {
    this.gallery    = document.querySelector('.product-gallery');
    this.mainImage  = document.getElementById('product-featured-image');
    this.thumbs     = document.querySelectorAll('.product-gallery__thumb');
    if (!this.gallery || !this.mainImage || this.thumbs.length === 0) return;
    this.init();
  }
  setActive(thumb) {
    this.thumbs.forEach((t) => t.classList.remove('active'));
    thumb.classList.add('active');
    const newSrc = thumb.dataset.src;
    if (newSrc) {
      this.mainImage.style.opacity = '0';
      this.mainImage.src = newSrc;
      this.mainImage.onload = () => { this.mainImage.style.opacity = '1'; };
    }
  }
  init() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.setActive(thumb));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.setActive(thumb); }
      });
    });
  }
}


/* ==========================================================
   6. Quantity Buttons (PDP)
   ========================================================== */

class QtyButtons {
  constructor() { this.init(); }
  init() {
    document.querySelectorAll('.product-form__qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.product-form__qty').querySelector('.product-form__qty-input');
        if (!input) return;
        const current = parseInt(input.value, 10) || 1;
        const change  = btn.textContent.trim() === '+' ? 1 : -1;
        const min     = parseInt(input.min, 10) || 1;
        input.value   = Math.max(min, current + change);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}


/* ==========================================================
   7. Variant Selector (PDP)
   ========================================================== */

class VariantSelector {
  constructor() {
    this.form = document.querySelector('.product-form');
    if (!this.form) return;
    this.priceEl     = this.form.closest('.product-info')?.querySelector('.product-info__price--current');
    this.compareEl   = this.form.closest('.product-info')?.querySelector('.product-info__price--compare');
    this.badgeEl     = this.form.closest('.product-info')?.querySelector('.product-info__badge');
    this.atcBtn      = this.form.querySelector('.product-form__atc');
    this.variantInput = this.form.querySelector('input[name="id"]');
    this.variants = window.__productVariants || [];
    this.init();
  }
  findVariant(selections) {
    return this.variants.find((v) => v.options.every((opt, i) => opt === selections[i]));
  }
  update(variant) {
    if (!variant) return;
    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = utils.formatMoney(variant.price);
    if (this.compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.compareEl.textContent = utils.formatMoney(variant.compare_at_price);
        this.compareEl.style.display = '';
        this.badgeEl && (this.badgeEl.style.display = '');
      } else {
        this.compareEl.style.display = 'none';
        this.badgeEl && (this.badgeEl.style.display = 'none');
      }
    }
    if (this.atcBtn) {
      if (variant.available) {
        this.atcBtn.disabled = false;
        this.atcBtn.textContent = 'Add to Cart';
        this.atcBtn.classList.remove('btn--disabled');
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'Sold Out';
        this.atcBtn.classList.add('btn--disabled');
      }
    }
    const gallery = new ProductGallery();
    if (variant.featured_image && gallery.mainImage) {
      gallery.mainImage.src = variant.featured_image.src;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }
  init() {
    if (this.variants.length === 0) return;
    this.form.querySelectorAll('.product-form__select').forEach((select) => {
      select.addEventListener('change', () => {
        const selections = Array.from(this.form.querySelectorAll('.product-form__select')).map((s) => s.value);
        const variant = this.findVariant(selections);
        if (variant) this.update(variant);
      });
    });
  }
}


/* ==========================================================
   8. Product Details Accordion (PDP)
   ========================================================== */

class ProductAccordion {
  constructor() {
    this.triggers = document.querySelectorAll('.product-details__trigger');
    if (!this.triggers.length) return;
    this.init();
  }
  init() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.product-details__item');
        const isOpen = item.classList.contains('is-open');
        document.querySelectorAll('.product-details__item.is-open').forEach((el) => {
          el.classList.remove('is-open');
          el.querySelector('.product-details__trigger').setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); trigger.click(); }
      });
    });
  }
}


/* ==========================================================
   9. Add-to-Cart Handler
   ========================================================== */

class AddToCart {
  constructor(cart) {
    this.cart = cart;
    this.init();
  }
  showFeedback(btn, success) {
    const original = btn.textContent;
    btn.textContent = success ? '✔ Added!' : '✘ Error';
    btn.disabled = true;
    setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1800);
  }
  pushAddToCart(item) {
    if (!item || !window.dataLayer) return;
    const price = (item.price || 0) / 100;
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: (item.line_price != null ? item.line_price : item.price * item.quantity) / 100,
        items: [{
          item_id: String(item.product_id ?? item.sku ?? item.id),
          item_name: item.product_title,
          item_variant: item.variant_title || undefined,
          price,
          quantity: item.quantity,
        }],
      },
    });
  }
  init() {
    const pdpForm = document.querySelector('.product-form[action="/cart/add"]');
    if (pdpForm) {
      pdpForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = pdpForm.querySelector('[name="add"]');
        if (!btn) return;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const body = {
            id: pdpForm.querySelector('[name="id"]')?.value,
            quantity: parseInt(pdpForm.querySelector('[name="quantity"]')?.value || '1', 10),
          };
          // S&S widget disables this input for one-time purchases
          const sellingPlan = pdpForm.querySelector('[name="selling_plan"]:not(:disabled)')?.value;
          if (sellingPlan) body.selling_plan = sellingPlan;
          const item = await utils.fetchJSON('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify(body),
          });
          this.pushAddToCart(item);
          this.showFeedback(btn, true);
          this.cart && this.cart.open();
        } catch (err) {
          console.warn('[ATC] failed:', err);
          this.showFeedback(btn, false);
        }
      });
    }
    document.querySelectorAll('.product-card__atc-form').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('.product-card__atc-btn');
        if (!btn) return;
        btn.disabled = true;
        const orig = btn.textContent;
        btn.textContent = 'Adding…';
        try {
          const item = await utils.fetchJSON('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify({ id: form.querySelector('[name="id"]')?.value, quantity: 1 }),
          });
          this.pushAddToCart(item);
          btn.textContent = '✔ Added!';
          this.cart && this.cart.open();
          setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 1800);
        } catch {
          btn.textContent = orig;
          btn.disabled = false;
        }
      });
    });
  }
}


/* ==========================================================
  10. Quick View
   ========================================================== */

class QuickView {
  constructor() {
    this.modal = null;
    this.init();
  }
  buildModal() {
    const modal = document.createElement('div');
    modal.id = 'quick-view-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Quick view');
    modal.style.cssText = 'display:none;position:fixed;inset:0;z-index:var(--z-modal);align-items:center;justify-content:center;padding:20px;';
    modal.innerHTML = `<div style="position:absolute;inset:0;background:var(--color-overlay);" class="qv-overlay"></div><div style="position:relative;background:var(--color-background);border-radius:var(--border-radius-lg);max-width:900px;width:100%;max-height:90vh;overflow:auto;padding:40px;" class="qv-inner"><button class="qv-close btn btn--secondary btn--sm" style="position:absolute;top:16px;right:16px;" aria-label="Close">✕</button><div class="qv-body">Loading…</div></div>`;
    document.body.appendChild(modal);
    modal.querySelector('.qv-overlay').addEventListener('click', () => this.close());
    modal.querySelector('.qv-close').addEventListener('click',   () => this.close());
    return modal;
  }
  async open(url) {
    if (!this.modal) this.modal = this.buildModal();
    this.modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    const body = this.modal.querySelector('.qv-body');
    body.innerHTML = 'Loading…';
    try {
      const res  = await fetch(url);
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const main = doc.querySelector('.product-main') || doc.querySelector('main');
      body.innerHTML = main ? main.innerHTML : '<p>Unable to load product.</p>';
    } catch {
      body.innerHTML = '<p>Unable to load product.</p>';
    }
  }
  close() {
    if (!this.modal) return;
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
  }
  init() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-view-url]');
      if (!btn) return;
      e.preventDefault();
      this.open(btn.dataset.quickViewUrl);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.style.display === 'flex') this.close();
    });
  }
}


/* ==========================================================
  11. Announcement Bar Dismiss
   ========================================================== */

class AnnouncementBar {
  constructor() {
    this.bar        = document.querySelector('.announcement-bar');
    this.dismissBtn = document.querySelector('[data-dismiss-announcement]');
    if (!this.bar || !this.dismissBtn) return;
    this.init();
  }
  init() {
    if (sessionStorage.getItem('announcement-dismissed')) {
      this.bar.style.display = 'none';
      return;
    }
    this.dismissBtn.addEventListener('click', () => {
      this.bar.style.display = 'none';
      sessionStorage.setItem('announcement-dismissed', '1');
    });
  }
}


/* ==========================================================
  12. CLI Docs
   ========================================================== */

class CliDocs {
  constructor() { this.init(); }
  initCopyButtons() {
    document.querySelectorAll('.cli-code-block__copy').forEach((btn) => {
      btn.addEventListener('click', () => {
        const block = btn.closest('.cli-code-block');
        const code  = block ? block.querySelector('code') : null;
        if (!code) return;
        navigator.clipboard.writeText(code.textContent.trim()).then(() => {
          const orig = btn.textContent;
          btn.textContent = 'Copied!';
          setTimeout(() => { btn.textContent = orig; }, 2000);
        }).catch(() => {
          const range = document.createRange();
          range.selectNode(code);
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
          document.execCommand('copy');
          window.getSelection().removeAllRanges();
        });
      });
    });
  }
  initActiveNav() {
    const sections = document.querySelectorAll('.cli-docs__section[id]');
    const links    = document.querySelectorAll('.cli-docs__nav-link');
    if (!sections.length || !links.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((l) => l.classList.remove('is-active'));
          const active = document.querySelector(`.cli-docs__nav-link[href="#${entry.target.id}"]`);
          active && active.classList.add('is-active');
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach((s) => obs.observe(s));
    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        const id = link.getAttribute('href');
        if (!id || !id.startsWith('#')) return;
        e.preventDefault();
        const target = document.querySelector(id);
        target && target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }
  init() { this.initCopyButtons(); this.initActiveNav(); }
}


/* ==========================================================
  13. Collection Sort Redirect
   ========================================================== */

class CollectionSort {
  constructor() {
    this.select = document.querySelector('.collection-sort select');
    if (!this.select) return;
    this.init();
  }
  init() {
    this.select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }
}


/* ==========================================================
  14. Cart Page
   ========================================================== */

class CartPage {
  constructor() {
    this.form = document.querySelector('form[action="/cart"]');
    if (!this.form) return;
    this.init();
  }
  init() {
    this.form.querySelectorAll('.product-form__qty-btn, .cart-item__qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const container = btn.closest('.product-form__qty, .cart-item__qty');
        const input = container?.querySelector('input[type="number"]');
        if (!input) return;
        const current = parseInt(input.value, 10) || 0;
        const change  = btn.textContent.trim() === '+' ? 1 : -1;
        input.value   = Math.max(0, current + change);
      });
    });
  }
}


/* ==========================================================
  15. Scroll-Reveal Animations
   ========================================================== */

class ScrollReveal {
  constructor() {
    if (!('IntersectionObserver' in window)) return;
    this.init();
  }
  init() {
    const style = document.createElement('style');
    style.textContent = '.reveal { opacity: 0; transform: translateY(20px); transition: opacity 0.5s ease, transform 0.5s ease; } .reveal.is-visible { opacity: 1; transform: none; }';
    document.head.appendChild(style);
    const els = document.querySelectorAll('.product-card, .collection-card, .article-card, .text-column, .section-header');
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add('is-visible'); obs.unobserve(entry.target); }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el, i) => {
      el.classList.add('reveal');
      el.style.transitionDelay = `${(i % 4) * 0.07}s`;
      obs.observe(el);
    });
  }
}


/* ==========================================================
  16. Predictive Search
   ========================================================== */

class PredictiveSearch {
  constructor() {
    this.toggle = document.querySelector('.search-toggle');
    if (!this.toggle) return;
    this.overlay = null;
    this.input   = null;
    this.results = null;
    this.init();
  }
  buildOverlay() {
    const el = document.createElement('div');
    el.id = 'search-overlay';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Search');
    el.innerHTML = `
      <div class="search-overlay__backdrop"></div>
      <div class="search-overlay__panel">
        <div class="search-overlay__inner">
          <div class="search-overlay__form">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input type="search" class="search-overlay__input" placeholder="Search products…" autocomplete="off" aria-label="Search products" aria-controls="search-results" aria-autocomplete="list">
            <button class="search-overlay__close" aria-label="Close search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="search-overlay__results" id="search-results" role="listbox" aria-label="Search results"></div>
        </div>
      </div>`;
    document.body.appendChild(el);
    return el;
  }
  open(prefill) {
    if (!this.overlay) {
      this.overlay = this.buildOverlay();
      this.input   = this.overlay.querySelector('.search-overlay__input');
      this.results = this.overlay.querySelector('.search-overlay__results');
      this.overlay.querySelector('.search-overlay__close').addEventListener('click', () => this.close());
      this.overlay.querySelector('.search-overlay__backdrop').addEventListener('click', () => this.close());
      this.input.addEventListener('input', utils.debounce((e) => this.search(e.target.value.trim()), 250));
      this.input.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      this.input.focus();
      if (prefill) { this.input.value = prefill; this.search(prefill); }
    }, 50);
  }
  close() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    this.results.innerHTML = '';
    this.input.value = '';
    this.toggle.focus();
  }
  async search(q) {
    if (q.length < 2) { this.results.innerHTML = ''; return; }
    const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag,variants.sku,variants.barcode`;
    try {
      const data = await utils.fetchJSON(url);
      this.render(data?.resources?.results?.products || [], q);
    } catch (e) {
      console.warn('[PredictiveSearch]', e);
    }
  }
  render(products, q) {
    if (!products.length) {
      this.results.innerHTML = `<p class="search-no-results">No results for "<strong>${q}</strong>"</p>`;
      return;
    }
    this.results.innerHTML =
      products.map(p => `
        <a href="${p.url}" class="search-result" role="option">
          <div class="search-result__img">
            ${p.image ? `<img src="${p.image}" alt="" loading="lazy" width="60" height="60">` : '<div class="search-result__img-placeholder"></div>'}
          </div>
          <div class="search-result__info">
            <p class="search-result__title">${p.title}</p>
          </div>
        </a>`).join('') +
      `<a href="/search?q=${encodeURIComponent(q)}&type=product&options[prefix]=last" class="search-view-all">View all results for "${q}" →</a>`;
  }
  init() {
    this.toggle.addEventListener('click', () => this.open());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay?.classList.contains('is-open')) this.close();
    });
    // Results-page on-page search box → launch the dynamic overlay (pre-filled),
    // so search behaves the same everywhere. Static form remains a no-JS fallback.
    const pageForm = document.querySelector('.search-results .search-form');
    if (pageForm) {
      const pageInput = pageForm.querySelector('.search-form__input');
      const launch = (e) => { e.preventDefault(); this.open(pageInput ? pageInput.value.trim() : ''); };
      if (pageInput) {
        pageInput.addEventListener('focus', launch);
        pageInput.addEventListener('click', launch);
      }
      pageForm.addEventListener('submit', launch);
    }
  }
}


/* ==========================================================
  17. Init
   ========================================================== */

document.addEventListener('DOMContentLoaded', () => {
  new StickyHeader();
  new MobileNav();
  const cart = new CartDrawer();
  new ProductGallery();
  new QtyButtons();
  new VariantSelector();
  new ProductAccordion();
  new AddToCart(cart);
  new QuickView();
  new AnnouncementBar();
  new CliDocs();
  new CollectionSort();
  new CartPage();
  new ScrollReveal();
  new PredictiveSearch();
});
